import { Link, Typography } from "@fullscript/aviary-web";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { l } from "@aviary/locales/i18n";
import { sharedRoutes } from "@shared/data/sharedRoutes";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import * as styles from "./FooterLinkList.styles";

interface Props {
  type?: "basic" | "product" | "checkout" | "labs";
  isWholesale?: boolean;
}

const FooterLinkList = ({ isWholesale, type = "basic" }: Props) => {
  const { t } = useTranslation();
  const { isUS, isPatient } = useSharedGlobalConfig();

  const getShippingPolicyLink = () => {
    return isWholesale
      ? sharedRoutes.external.shippingPolicyWholesale
      : sharedRoutes.external.shippingPolicyRetail;
  };

  const getTermsOfServiceLink = () => {
    return isPatient
      ? sharedRoutes.external.termsOfServicePatients
      : sharedRoutes.external.termsOfService;
  };

  const renderLinkItem = (href: string, label: string, key: string) => (
    <li key={key}>
      <Typography variant="footnote" intention="neutral" colorIntensity="subtle">
        <Link hideExternalIcon hideUnderline href={href}>
          {label}
        </Link>
      </Typography>
    </li>
  );

  const linkItems: ReactNode[] = [
    renderLinkItem(getTermsOfServiceLink(), t(l.aviary.footer.TermsOfService), "terms-of-service"),
    renderLinkItem(
      sharedRoutes.external.privacyPolicy,
      t(l.aviary.footer.PrivacyStatement),
      "privacy-statement"
    ),
  ];

  if (type !== "checkout" && !isUS) {
    linkItems.push(
      renderLinkItem(
        sharedRoutes.external.accessibility,
        t(l.aviary.footer.AccessibilityPolicy),
        "accessibility-policy"
      )
    );
  }

  if (isUS) {
    linkItems.push(
      renderLinkItem(
        sharedRoutes.external.privacyRightsNotice,
        t(l.aviary.footer.PrivacyRightsNotice),
        "privacy-rights-notice"
      )
    );
  }

  if (type === "checkout") {
    linkItems.push(
      renderLinkItem(getShippingPolicyLink(), t(l.aviary.footer.ShippingPolicy), "shipping-policy"),
      renderLinkItem(
        sharedRoutes.external.refundPolicy,
        t(l.aviary.footer.RefundPolicy),
        "refund-policy"
      )
    );
  }

  linkItems.push(
    renderLinkItem(
      sharedRoutes.external.support,
      t(l.aviary.footer.HelpAndSupport),
      "help-and-support"
    )
  );

  return <ul css={styles.list}>{linkItems}</ul>;
};

export { FooterLinkList };
